import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
// @mui
import { useTheme, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
// utils
import { fCurrencyEuro, fPercent, fPercentEuro } from '../../utils/format-number';
// theme
import { bgGradient } from '../../theme/css';
// components
import Iconify from '../../components/iconify';
import Chart, { useChart } from '../../components/chart';
import CustomPopover, { usePopover } from '../../components/custom-popover';

// ----------------------------------------------------------------------

export default function FinancialWidgetSummary({
  title,
  bigtitle,
  total,
  icon,
  percent,
  color,
  chart,
  sx,
  ...other
}) {
  const theme = useTheme();
  const {series, options} = chart || {series: null, options: null};

  // const today = new Date();
  // const currentMonth = today.getMonth(); 
  // const currentYear = today.getFullYear();


  let latestDate = new Date();
  if (series && series.length > 0) {
    const lastX = series[series.length - 1].x;
    latestDate = new Date(lastX);
  }

  function getMinMonthYear(monthsAgo) {
    const baseDate = new Date(latestDate);
    const date = new Date(baseDate.getFullYear(), baseDate.getMonth() - monthsAgo, 1);
    return { minMonth: date.getMonth(), minYear: date.getFullYear() };
  }

  const seriesFilter = [
    { label: "12 meses", ...getMinMonthYear(11) },
    { label: "6 meses", ...getMinMonthYear(5) },
    { label: "3 meses", ...getMinMonthYear(2) },
    { label: "1 mes", ...getMinMonthYear(0) },
  ];

  const [seriesData, setSeriesData] = useState('12 meses');

  const popover = usePopover();

  const handleChangeSeries = useCallback(
    (newValue) => {
      popover.onClose();
      setSeriesData(newValue);
    },
    [popover]
  );

  // Filtrar la serie según selección
  let filteredSeries = null;
  if (series){
    filteredSeries = series.filter(({ x }) => {
      const { minMonth, minYear } = seriesFilter.find(option => option.label === seriesData);
      const date = new Date(x); // x es tipo "2024-09-16"

      // Verifica si la fecha es >= a la mínima permitida
      return (
        date.getFullYear() > minYear ||
        (date.getFullYear() === minYear && date.getMonth() >= minMonth)
      );
    });
  }


  const optionsBase = {
  ...(color && { colors: [theme.palette[color].main] }),
  chart: {
    sparkline: {
      enabled: false,
    },
    height: 400,
  },
  xaxis: {
    type: 'datetime',
    labels: {
      ...(color && {
        style: {
          colors: theme.palette[color].dark,
        },
      }),
      show: true,
      rotate: -45,
      trim: false,
      formatter(value) {
        const date = new Date(value);
        if (seriesData !== "1 mes") {
          if (date.getDate() !== 1) return '';
          return date.toLocaleDateString('es-ES', {
            month: 'short',
            year: '2-digit',
          });
        }
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short',
        });
      },
    },
    tickAmount: seriesData === "1 mes" ? (filteredSeries?.length || 1) : 'dataPoints',
  },
  yaxis: {
    labels: {
      formatter: (value) => fCurrencyEuro(value || '0'),
      ...(color && {
        style: {
          colors: theme.palette[color].dark,
        },
      }),
      show: true,
    },
  },
  legend: {
    show: false,
  },
  grid: {
    show: true,
    padding: {
      bottom: 30,
    },
  },
  tooltip: {
    marker: {
      show: false,
    },
    x: {
      formatter(value) {
        const date = new Date(value);
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      },
    },
    y: {
      formatter: (value) => fCurrencyEuro(value || '0'),
      title: {
        formatter: () => '',
      },
    },
  },
  ...options,
};

const chartOptions = useChart(optionsBase);

  return (
    <Stack
      sx={{
        ...color && bgGradient({
          direction: '135deg',
          startColor: alpha(theme.palette[color].light, 0.8),
          endColor: alpha(theme.palette[color].main, 0.8),
        }),
        width: 1,
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        color: `${color}.darker`,
        backgroundColor: 'common.white',
        ...sx,
      }}
      {...other}
    >
      {icon && (
        <Iconify
          icon={icon}
          sx={{
            p: 1.5,
            top: 10,
            right: 10,
            width: 80,
            height: 80,
            borderRadius: '50%',
            position: 'absolute',
          }}
        />
      )}
      {series && <ButtonBase
        onClick={popover.onOpen}
        sx={{
          px: 2,
          py: 1,
          top: 25,
          right: 25,
          borderRadius: 1,
          position: 'absolute',
          typography: 'subtitle2',
          // bgcolor: `${color}.lighter`,
          bgcolor: theme.palette.grey[200],
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {seriesData}

        <Iconify
          width={16}
          icon={popover.open ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
          sx={{ ml: 0.5 }}
        />
      </ButtonBase>}

      <Stack spacing={1} sx={{ p: 3 }}>
        <Typography variant={bigtitle ? "h4" : "subtitle2"}>{title}</Typography>

        {total && <Typography variant="h3">{fCurrencyEuro(total || '0')}</Typography>}

        {(percent && (percent !== -Infinity && percent !== Infinity)) ?
          <Stack
            spacing={0.5}
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            sx={{typography: 'body2'}}
          >
            <Iconify icon={percent < 0 ? 'eva:trending-down-fill' : 'eva:trending-up-fill'} color={percent < 0 ? theme.palette.red.main : theme.palette.tertiary.main} />

            <Box sx={{typography: 'subtitle2'}}>
              {percent > 0 && '+'}
              {fPercentEuro(percent)}
            </Box>

            <Box sx={{opacity: 0.8}}>que el mes anterior</Box>
          </Stack>
          :
          <></>
          }
      </Stack>
      {chart && (
      <Box>
        <Chart type="area" series={[{ data: filteredSeries || series }]} options={chartOptions} height={400} width='100%'/>
      </Box>
      )}

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 140 }}>
        {seriesFilter.map((option) => (
          <MenuItem
            key={option.label}
            selected={option.label === seriesData}
            onClick={() => handleChangeSeries(option.label)}
          >
            {option.label}
          </MenuItem>
        ))}
      </CustomPopover>
    </Stack>
  );
}

FinancialWidgetSummary.propTypes = {
  chart: PropTypes.object,
  color: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  percent: PropTypes.number,
  sx: PropTypes.object,
  title: PropTypes.string,
  bigtitle: PropTypes.bool,
  total: PropTypes.number,
};
